import React, { createContext, useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import AuthContext from "./AuthContext";
import { useApi } from "../hooks/useApi";

const API_BASE_URL = process.env.REACT_APP_API_ROUTE;
const BRANDING_ENDPOINT = "/dashboard/branding";

const EMPTY_BRANDING = {
  logoUrl: null,
  logoLevel: null,
  editableLevel: null,
  ownLogoUrl: null,
};

const toBranding = (data) => ({
  logoUrl: data?.logo_url || null,
  logoLevel: data?.logo_level || null,
  editableLevel: data?.editable_level || null,
  ownLogoUrl: data?.own_logo_url || null,
});

const BrandingContext = createContext({
  ...EMPTY_BRANDING,
  loaded: false,
  uploadLogo: async () => {},
  removeLogo: async () => {},
});

// Logo del dashboard resuelto por el backend (merchant -> ISO -> adquirente).
// logoUrl en null significa usar el logo de Blokko.
export const BrandingProvider = ({ children }) => {
  const { token, authenticated } = useContext(AuthContext);
  const { get } = useApi();
  const [branding, setBranding] = useState(EMPTY_BRANDING);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!authenticated || !token) return;
    let cancelled = false;
    get(BRANDING_ENDPOINT)
      .then((data) => {
        if (!cancelled) setBranding(toBranding(data));
      })
      .catch(() => {
        // Sin marca configurada o error: se queda el logo de Blokko.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // Solo al iniciar sesión; el token se refresca sin cambiar la marca.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  const uploadLogo = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append("logo", file);
      // Sin Content-Type: el navegador lo arma con el boundary del FormData.
      const response = await axios.put(
        `${API_BASE_URL}${BRANDING_ENDPOINT}/logo`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBranding(toBranding(response.data));
    },
    [token],
  );

  const removeLogo = useCallback(async () => {
    const response = await axios.delete(
      `${API_BASE_URL}${BRANDING_ENDPOINT}/logo`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setBranding(toBranding(response.data));
  }, [token]);

  return (
    <BrandingContext.Provider
      value={{ ...branding, loaded, uploadLogo, removeLogo }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
